import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const COLUMN_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number Input' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'select', label: 'Dropdown Select' },
];

// Default values for row data structure
const DEFAULT_ROW_DATA = {
  __rowName: '',
  __defaults: {},
};

const getColumnTypeLabel = (type) => COLUMN_TYPES.find(t => t.value === type)?.label || type;

const renderCellValue = (value, type) => {
  if (value === '' || value === null || value === undefined) return '-';
  if (type === 'yes_no') return value === 'yes' || value === true ? '✓ Yes' : '✗ No';
  return String(value);
};

// Debug logging utility
const debugLog = (section, message, data) => {
  console.log(`[TableGridOptions:${section}]`, message, data);
};

function InputCell({ column, value, onChange, editing }) {
  const { t } = useTranslation(['procedures', 'common']);

  if (!editing) {
    return renderCellValue(value, column.type);
  }

  const commonProps = {
    size: 'small',
    variant: 'outlined',
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    fullWidth: true,
    sx: { minWidth: 100 },
  };

  switch (column.type) {
    case 'number':
      return <TextField {...commonProps} type="number" />;
    case 'yes_no':
      return (
        <Select {...commonProps}>
          <MenuItem value="yes">{t('procedures:step_options.yes')}</MenuItem>
          <MenuItem value="no">{t('procedures:step_options.no')}</MenuItem>
        </Select>
      );
    case 'select':
      return (
        <Select {...commonProps}>
          {column.options?.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      );
    default:
      return <TextField {...commonProps} />;
  }
}

export default function TableGridOptions({ data, updateOptions }) {
  const { t } = useTranslation(['procedures', 'common']);
  const options = data.options || {};
  const columns = options.columns || [];
  const rows = options.rows || [];

  // Editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, colId }
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [editingColumnIdx, setEditingColumnIdx] = useState(null);
  const [columnForm, setColumnForm] = useState({
    id: '',
    label: '',
    type: 'text',
    options: [],
  });

  // ===== Column Management =====
  const generateColumnId = (label) => {
    return label
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^\w_]/g, '')
      .slice(0, 50) || `col_${Date.now()}`;
  };

  const handleAddColumn = () => {
    setColumnForm({ id: '', label: '', type: 'text', options: [] });
    setEditingColumnIdx(null);
    setColumnSettingsOpen(true);
  };

  const handleEditColumn = (idx) => {
    setColumnForm({ ...columns[idx] });
    setEditingColumnIdx(idx);
    setColumnSettingsOpen(true);
  };

  const handleSaveColumn = () => {
    debugLog('handleSaveColumn', 'Starting save', { isEditing: editingColumnIdx !== null });

    if (!columnForm.label?.trim()) {
      alert('Column label is required');
      return;
    }

    // When creating a new column, generate ID. When editing, NEVER change the ID
    let columnId;
    if (editingColumnIdx === null) {
      // Creating new column
      columnId = generateColumnId(columnForm.label);
      debugLog('handleSaveColumn', 'Generated new column ID', { columnId });
    } else {
      // Editing existing column - preserve the original ID
      columnId = columns[editingColumnIdx].id;
      debugLog('handleSaveColumn', 'Preserving existing column ID', { columnId, originalId: columns[editingColumnIdx].id });
    }

    const updatedColumn = { ...columnForm, id: columnId };
    const updatedColumns = editingColumnIdx !== null
      ? columns.map((col, idx) => (idx === editingColumnIdx ? updatedColumn : col))
      : [...columns, updatedColumn];

    debugLog('handleSaveColumn', 'Column saved', { updatedColumn, totalColumns: updatedColumns.length });
    updateOptions({ columns: updatedColumns });
    setColumnSettingsOpen(false);
  };

  const handleDeleteColumn = (idx) => {
    const deletedColumnId = columns[idx].id;
    debugLog('handleDeleteColumn', 'Deleting column', { colIdx: idx, columnId: deletedColumnId, columnLabel: columns[idx].label });
    const updatedColumns = columns.filter((_, i) => i !== idx);
    const updatedRows = rows.map(row => {
      const newRow = { ...row };
      if (newRow.__defaults) {
        delete newRow.__defaults[deletedColumnId];
      }
      return newRow;
    });
    debugLog('handleDeleteColumn', 'Column deleted and removed from all rows', { totalColumns: updatedColumns.length, totalRows: updatedRows.length });
    updateOptions({ columns: updatedColumns, rows: updatedRows });
  };

  // ===== Row Management =====
  const handleAddRow = () => {
    const newRow = { ...DEFAULT_ROW_DATA };
    columns.forEach(col => {
      newRow.__defaults[col.id] = '';
    });
    const updatedRows = [...rows, newRow];
    debugLog('handleAddRow', 'Row added', { newRow, totalRows: updatedRows.length });
    updateOptions({ rows: updatedRows });
    setEditingRowIdx(updatedRows.length - 1);
  };

  const handleDeleteRow = (idx) => {
    debugLog('handleDeleteRow', 'Deleting row', { rowIdx: idx, deletedRow: rows[idx] });
    const updatedRows = rows.filter((_, i) => i !== idx);
    debugLog('handleDeleteRow', 'Row deleted', { totalRows: updatedRows.length });
    updateOptions({ rows: updatedRows });
  };

  const handleCellChange = (rowIdx, colId, value) => {
    const updatedRows = rows.map((row, idx) => {
      if (idx === rowIdx) {
        if (colId === '__rowName') {
          debugLog('handleCellChange', 'Row name changed', { rowIdx, newValue: value });
          return { ...row, __rowName: value };
        }
        debugLog('handleCellChange', 'Cell value changed', { rowIdx, colId, newValue: value });
        return {
          ...row,
          __defaults: {
            ...row.__defaults,
            [colId]: value,
          },
        };
      }
      return row;
    });
    updateOptions({ rows: updatedRows });
  };

  const handleDuplicateRow = (idx) => {
    const newRow = { ...rows[idx] };
    debugLog('handleDuplicateRow', 'Duplicating row', { sourceRowIdx: idx, newRow });
    const updatedRows = [...rows.slice(0, idx + 1), newRow, ...rows.slice(idx + 1)];
    debugLog('handleDuplicateRow', 'Row duplicated', { totalRows: updatedRows.length });
    updateOptions({ rows: updatedRows });
  };

  // ===== Rendering =====
  const emptyColumnState = columns.length === 0;

  return (
    <Box sx={{ mt: 2 }}>
      {emptyColumnState ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('procedures:form.no_columns_message')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddColumn}
            >
              {t('procedures:form.add_first_column')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Column Configuration Bar */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
            }}
          >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 'auto' }}>
          {t('procedures:form.table_columns')}: {columns.length}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {columns.map((col, idx) => (
            <Chip
              key={col.id}
              label={`${col.label} (${getColumnTypeLabel(col.type)})`}
              onDelete={() => handleDeleteColumn(idx)}
              onClick={() => handleEditColumn(idx)}
              icon={<EditIcon />}
              size="small"
              variant="outlined"
            />
          ))}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddColumn}
          >
            {t('procedures:form.add_column')}
          </Button>
        </Box>
      </Box>

      {/* Table Preview & Editor */}
      <Card>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, minWidth: 120, backgroundColor: '#fafafa' }}>
                    {t('procedures:form.row_name')}
                  </TableCell>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        fontWeight: 600,
                        minWidth: 120,
                        backgroundColor: '#fafafa',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f0f0f0' },
                      }}
                      onClick={() => handleEditColumn(columns.indexOf(col))}
                      title="Click to edit column"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {col.label}
                        <Chip
                          label={getColumnTypeLabel(col.type)}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 60, textAlign: 'center' }}>{t('common:actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 2} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('procedures:form.no_rows_message')}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                      >
                        {t('procedures:form.add_first_row')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, rowIdx) => (
                    <TableRow
                      key={rowIdx}
                      sx={{
                        backgroundColor: editingRowIdx === rowIdx ? '#f9f9f9' : 'inherit',
                        '&:hover': { backgroundColor: '#fafafa' },
                      }}
                    >
                      {/* Row Name */}
                      <TableCell
                        sx={{
                          p: 0.5,
                          backgroundColor: editingCell?.rowIdx === rowIdx && editingCell?.colId === '__rowName'
                            ? '#e3f2fd'
                            : 'inherit',
                        }}
                        onClick={() => setEditingCell({ rowIdx, colId: '__rowName' })}
                      >
                        {editingCell?.rowIdx === rowIdx && editingCell?.colId === '__rowName' ? (
                          <Box sx={{ p: 0.5 }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              value={row.__rowName || ''}
                              onChange={(e) => handleCellChange(rowIdx, '__rowName', e.target.value)}
                              fullWidth
                              sx={{ minWidth: 100 }}
                              autoFocus
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              p: 0.5,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#e3f2fd' },
                              fontWeight: 500,
                            }}
                          >
                            {row.__rowName || '-'}
                          </Box>
                        )}
                      </TableCell>

                      {/* Data Cells */}
                      {columns.map((col) => (
                        <TableCell
                          key={`${rowIdx}-${col.id}`}
                          sx={{
                            p: 0.5,
                            backgroundColor: editingCell?.rowIdx === rowIdx && editingCell?.colId === col.id
                              ? '#e3f2fd'
                              : 'inherit',
                          }}
                          onClick={() => setEditingCell({ rowIdx, colId: col.id })}
                        >
                          {editingCell?.rowIdx === rowIdx && editingCell?.colId === col.id ? (
                            <Box sx={{ p: 0.5 }}>
                              <InputCell
                                column={col}
                                value={row.__defaults?.[col.id]}
                                onChange={(value) => {
                                  handleCellChange(rowIdx, col.id, value);
                                }}
                                editing={true}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                p: 0.5,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#e3f2fd' },
                              }}
                            >
                              {renderCellValue(row.__defaults?.[col.id], col.type)}
                            </Box>
                          )}
                        </TableCell>
                      ))}

                      {/* Row Actions */}
                      <TableCell sx={{ textAlign: 'center', width: 60 }}>
                        <Tooltip title="Duplicate row">
                          <IconButton
                            size="small"
                            onClick={() => handleDuplicateRow(rowIdx)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete row">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRow(rowIdx)}
                            color="error"
                            sx={{ p: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add Row Button */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
            >
              {t('procedures:form.add_row')}
            </Button>
          </Box>
        </CardContent>
      </Card>
        </>
      )}

      {/* Column Settings Dialog */}
      <Dialog
        open={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingColumnIdx !== null ? t('procedures:form.edit_column') : t('procedures:form.add_column')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('procedures:form.column_label')}
              size="small"
              value={columnForm.label}
              onChange={(e) => setColumnForm({ ...columnForm, label: e.target.value })}
              placeholder="e.g., Status"
              helperText={t('procedures:form.column_label_help')}
              autoFocus
            />
            <FormControl fullWidth size="small">
              <InputLabel id="table-grid-options-input-type-label">{t('procedures:form.input_type')}</InputLabel>
              <Select labelId="table-grid-options-input-type-label"
                value={columnForm.type}
                label="Input Type"
                onChange={(e) => setColumnForm({ ...columnForm, type: e.target.value })}
              >
                {COLUMN_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {columnForm.type === 'select' && (
              <TextField
                fullWidth
                label="Select Options"
                size="small"
                value={Array.isArray(columnForm.options) ? columnForm.options.join(', ') : ''}
                onChange={(e) =>
                  setColumnForm({
                    ...columnForm,
                    options: e.target.value
                      .split(',')
                      .map(o => o.trim())
                      .filter(o => o),
                  })
                }
                placeholder="Option 1, Option 2, Option 3"
                helperText="Comma-separated values"
                multiline
                minRows={2}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnSettingsOpen(false)}>{t('procedures:form.cancel')}</Button>
          <Button onClick={handleSaveColumn} variant="contained">
            {t('procedures:form.save_column')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
